import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CreateNewNodesService } from '../../services/create-new-nodes.service';
import { NodeFactory } from '@modules/campaigns/Domain/Factories/NodeFactory';
import { WorkflowClass } from '@modules/campaigns/Domain/WorkflowClass';
import {
  draggableMenu,
  draggables,
  mapColorsNode,
} from '@shared/components/joint-js/config/constants';
import { CollectorChannelService } from '@shared/services/channelConfigCollectors.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { take } from 'rxjs';

type PaletteShape = 'circle' | 'pill' | 'diamond' | 'hexagon' | 'pentagon';

interface GraphContext {
  graph?: { addCell: (cell: any) => void } | null;
  paper?: {
    clientToLocalPoint: (x: number, y: number) => { x: number; y: number };
  } | null;
  selectedElement?: any;
}

interface DraggableNode {
  id: string;
  name: string;
  class: string;
  isActive: boolean;
}

interface PaletteSection {
  id: string;
  icon: string;
  titleKey: string;
  nodes: DraggableNode[];
}

@Component({
  selector: 'app-node-drag-drop',
  templateUrl: './node-drag-drop.component.html',
  styleUrls: ['./node-drag-drop.component.scss'],
})
export class NodeDragDropComponent implements OnInit {
  @Input() graph?: GraphContext;
  @Input() workflow: WorkflowClass | undefined;
  @Output() nodeDrag = new EventEmitter<any>();

  draggables: draggableMenu = draggables;
  paletteSections: PaletteSection[] = [];
  showWarningModal = false;
  currentChannel = '';
  warningType: 'channel' | 'waitNode' | null = null;
  collapsedSections = new Set<string>();

  private aiNodeIds = new Set<string>();
  private readonly nodeStrokeColorMap: Record<string, string> = mapColorsNode;
  private readonly nodeTextColorOverrides: Record<string, string> = {
    createMasc: '#4c0370',
  };

  constructor(
    private createNewNodes: CreateNewNodesService,
    private collectorChannel: CollectorChannelService,
  ) {}

  ngOnInit() {
    this.draggables = this.createNewNodes.translateDraggables(draggables);
    this.aiNodeIds = new Set(this.draggables.actionPlus.map((node: DraggableNode) => node.id));
    this.buildPaletteSections();
    this.collapsedSections = new Set(this.paletteSections.map((section) => section.id));

    this.collectorChannel
      .loadData()
      .pipe(take(1))
      .subscribe({
        error: (err) => {
          console.error('Failed to load collector data', err);
        },
      });
  }

  drop(event: CdkDragDrop<string[]>) {
    const type = event.item.element.nativeElement.id as string;

    if (!type) {
      this.handleError('Node type is not defined.');
      return;
    }

    const graphCtx = this.graph;
    const paper = graphCtx?.paper;
    const graphModel = graphCtx?.graph;

    if (!this.workflow || !paper || !graphModel) {
      this.handleError('Workflow or graph context is not available.');
      return;
    }

    const typeNode = this.stringToNodeType(type);

    if (this.isChannelDisabled(type)) {
      this.openWarning('channel', type);
    }

    if (typeNode && !this.workflow.canAddWaitTypeNode(typeNode)) {
      this.openWarning('waitNode');
      return;
    }

    const localPoint = paper.clientToLocalPoint(event.dropPoint.x, event.dropPoint.y);

    try {
      const newNode = this.createNewNodes.createNode(type, localPoint.x, localPoint.y);
      if (!newNode) {
        this.handleError('Failed to create a new node.');
        return;
      }

      const newDNodeID = newNode?.attributes?.['data']?.original;
      const nodeType = NodeFactory.mapStringToNodeType(type);
      if (nodeType && newDNodeID) {
        const newDNode = NodeFactory.createNode(
          nodeType,
          localPoint.x,
          localPoint.y,
          newDNodeID.toString(),
        );

        if (newDNode && this.workflow) {
          newDNode.initNodeConfig();
          this.workflow.addNode(newDNode);
        } else {
          this.handleError('Error: Could not add node to workflow.');
        }
      } else {
        this.handleError('Error: Node type or ID is missing.');
      }

      graphModel.addCell(newNode);
      if (graphCtx) {
        graphCtx.selectedElement = newNode;
      }
      this.nodeDrag.emit(newNode);
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.handleError('An unexpected error occurred: ' + error.message);
      } else {
        this.handleError('An unexpected error occurred: ' + String(error));
      }
    }
  }

  isChannelDisabled(channel: string): boolean {
    const notFullyUsedChannels = this.collectorChannel.getChannelsNotFullyUsed();
    const normalizedChannel = this.normalizeChannelKey(channel);
    return notFullyUsedChannels.includes(normalizedChannel);
  }

  normalizeChannelKey(channel: string): string {
    const noSuper = channel.replace(/^super/, '');
    return noSuper.charAt(0).toLowerCase() + noSuper.slice(1);
  }

  trackBySection(index: number, section: PaletteSection): string {
    return `${section.id}-${index}`;
  }

  trackByNode(_: number, node: DraggableNode): string {
    return node.id;
  }

  isSectionCollapsed(sectionId: string): boolean {
    return this.collapsedSections.has(sectionId);
  }

  toggleSection(sectionId: string): void {
    if (this.isSectionCollapsed(sectionId)) {
      this.collapsedSections.delete(sectionId);
      return;
    }

    this.collapsedSections.add(sectionId);
  }

  isAiNode(nodeId: string): boolean {
    return this.aiNodeIds.has(nodeId);
  }

  getNodeShape(nodeId: string): PaletteShape {
    if (
      nodeId === 'paylinkNotificationStatus' ||
      nodeId === 'customerCaseSegments' ||
      nodeId === 'experiment'
    ) {
      return 'diamond';
    }

    if (nodeId === 'offerPartialPayment') {
      return 'hexagon';
    }

    if (nodeId === 'offerPaymentAgreement') {
      return 'pentagon';
    }

    if (
      nodeId === 'waitDays' ||
      nodeId === 'waitDayOfWeek' ||
      nodeId === 'waitDaysForDueDate' ||
      nodeId === 'waitDayOfMonth' ||
      nodeId === 'end'
    ) {
      return 'circle';
    }

    return 'pill';
  }

  getNodeColor(nodeId: string): string {
    return this.nodeStrokeColorMap[nodeId] ?? '#64748b';
  }

  getNodeTextColor(nodeId: string): string {
    return this.nodeTextColorOverrides[nodeId] ?? this.getNodeColor(nodeId);
  }

  isFlexPolygonNode(nodeId: string): boolean {
    return nodeId === 'offerPartialPayment' || nodeId === 'offerPaymentAgreement';
  }

  getFlexNodeViewBox(nodeId: string): string {
    return nodeId === 'offerPartialPayment' ? '0 0 100 100' : '35 10 80 80';
  }

  getFlexNodePolygonPoints(nodeId: string): string {
    if (nodeId === 'offerPartialPayment') {
      return '50,0 93.3,25 93.3,75 50,100 6.7,75 6.7,25';
    }

    return '75,90 115,50 95,10 55,10 35,50';
  }

  private stringToNodeType(typeStr: string): NodeType | undefined {
    return Object.values(NodeType).find((t) => t === typeStr);
  }

  private buildPaletteSections() {
    this.paletteSections = [
      {
        id: 'events',
        icon: 'pi pi-stopwatch',
        titleKey: 'DATA.MODULES.CAMPAINGS.WORKFLOWS.NODE_TYPE.WAITS',
        nodes: this.getActiveNodes(this.draggables.events),
      },
      {
        id: 'notifications',
        icon: 'pi pi-check-circle',
        titleKey: 'DATA.MODULES.CAMPAINGS.WORKFLOWS.NODE_TYPE.NOTIFICATIONS',
        nodes: this.getActiveNodes([...this.draggables.actions, ...this.draggables.actionPlus]),
      },
      {
        id: 'conditionals',
        icon: 'pi pi-sitemap',
        titleKey: 'DATA.MODULES.CAMPAINGS.WORKFLOWS.NODE_TYPE.CONDITIONAL',
        nodes: this.getActiveNodes(this.draggables.conditions),
      },
      {
        id: 'flexibility',
        icon: 'pi pi-th-large',
        titleKey: 'DATA.MODULES.CAMPAINGS.WORKFLOWS.NODE_TYPE.FLEX_OPTIONS',
        nodes: this.getActiveNodes(this.draggables.flexibility),
      },
      {
        id: 'calls',
        icon: 'pi pi-phone',
        titleKey: 'DATA.MODULES.CAMPAINGS.WORKFLOWS.NODE_TYPE.CALL_OPTIONS',
        nodes: this.getActiveNodes(this.draggables.calls),
      },
      {
        id: 'legalClaim',
        icon: 'pi pi-megaphone',
        titleKey: 'DATA.MODULES.CAMPAINGS.WORKFLOWS.NODE_TYPE.LEGAL_CLAIM',
        nodes: this.getActiveNodes(this.draggables.createLegalClaim),
      },
      {
        id: 'end',
        icon: 'pi pi-flag',
        titleKey: 'DATA.MODULES.CAMPAINGS.WORKFLOWS.NODE_TYPE.END',
        nodes: this.getActiveNodes([...this.draggables.end, ...this.draggables.assign]),
      },
    ];
  }

  private getActiveNodes(nodes: any[]): DraggableNode[] {
    return (nodes ?? []).filter((node) => node.isActive !== false);
  }

  private handleError(message: string) {
    console.error(message);
  }

  private openWarning(type: 'channel' | 'waitNode', channel = ''): void {
    this.warningType = type;
    this.showWarningModal = true;
    this.currentChannel = channel;
  }
}
